import {
  ACCEPTED_VIDEO_FORMATS,
  MAX_ENDING_URL_CHARS,
  MAX_UNCROPED_WEBHOOK_URL_CHARS,
  THREE_DOTS_LENGTH,
} from './constants';
import { urlPattern } from './validation/patterns';

/**
 * Checks if a string matches the URL pattern.
 *
 * @param {string} url - The URL to validate.
 * @returns {boolean} - True if valid URL, otherwise false.
 */
export const isUrl = (url) => urlPattern.test(url);

/**
 * Determines if a URL is a video based on its file extension against accepted formats.
 *
 * @param {string} url - The URL to check.
 * @returns {boolean} - Returns true if the URL is a video.
 */
export const isVideo = (url: string) => {
  if (!url) {
    return false;
  }

  const extensionPattern = /\.([0-9a-z]+)(?=[?#]|$)/i;
  const extension = url.match(extensionPattern);

  if (!extension) {
    return false;
  }

  for (const format in ACCEPTED_VIDEO_FORMATS) {
    if (ACCEPTED_VIDEO_FORMATS[format].includes(extension[0].toLowerCase())) {
      return true;
    }
  }

  return false;
};

/**
 * Renders a URL cropped to fit within specified character limits while preserving its end.
 *
 * @param {string} [url] - The URL to crop.
 * @param {number} [maxVisible=MAX_UNCROPED_WEBHOOK_URL_CHARS] - Maximum visible characters.
 * @param {number} [maxEnding=MAX_ENDING_URL_CHARS] - Maximum characters to keep at the end.
 * @returns {string} - The cropped URL.
 */
export const renderCroppedURL = (
  url?: string,
  maxVisible = MAX_UNCROPED_WEBHOOK_URL_CHARS,
  maxEnding = MAX_ENDING_URL_CHARS
) => {
  if (!url) {
    return '';
  }

  if (url.length <= maxVisible) {
    return url;
  }

  let formattedUrl = url;
  if (url[url.length - 1] === '/') {
    formattedUrl = url.slice(0, -1);
  }

  const urlParts = formattedUrl.split('/');
  const finalUrlPart = urlParts[urlParts.length - 1];

  const endingURLCharacters: number = Math.min(finalUrlPart.length, maxEnding);
  const startingURLCharacters = maxVisible - THREE_DOTS_LENGTH - endingURLCharacters;

  const croppedURL = `${formattedUrl.slice(
    0,
    startingURLCharacters
  )}.../${formattedUrl.slice(-endingURLCharacters)}`;

  return croppedURL;
};

const CAROUSEL_PLACEHOLDER_BASE =
  'https://res.cloudinary.com/dey0ylu2x/image/upload/v1607095100/carousel/';

/**
 * Fetches a URL for a carousel placeholder image.
 *
 * @param {Object} param - An object containing type and id.
 * @param {string} param.type - The type of the carousel.
 * @param {string} param.id - The unique identifier.
 * @returns {string} - The URL for the carousel placeholder image.
 */
export const getCarouselPlaceholder = ({ type = 'image', id }) =>
  `${CAROUSEL_PLACEHOLDER_BASE}${type}${id}.png`;

export const isCarouselPlaceholder = (url?: string): boolean =>
  !!url && url.startsWith(CAROUSEL_PLACEHOLDER_BASE);
